import Highcharts from 'highcharts';
import { formatCurrency } from '../../../../../../CommonUtils/formatNumber';
import { useTheme } from '../../../../../../theme/theme';
import { ChartDataDTO } from '../../../../../api/useDecumulationGoalQuery';

export enum PlanSeriesName {
  Covered = 'Covered',
  NotCovered = 'NotCovered',
}

interface Params {
  data: ChartDataDTO;
}

export const useCharitableGivingChart = ({ data }: Params) => {
  const theme = useTheme();

  const { Categories, Series } = data;

  const seriesStackOrder = [PlanSeriesName.NotCovered, PlanSeriesName.Covered];

  const chartSeries = seriesStackOrder.map(seriesName => ({
    name: seriesName,
    color:
      seriesName === PlanSeriesName.Covered
        ? theme.colors.primary['300']
        : theme.colors.primary['100'],

    data: Series[seriesName].map((series, index) => ({
      x: parseInt(Categories[index], 10),
      y: series,
    })),
  }));

  const chartConfig: Highcharts.Options = {
    credits: {
      enabled: false,
    },
    title: undefined,
    chart: {
      type: 'column',
      height: 430,
      spacingRight: 20,
      spacingBottom: 20,
      spacingTop: 50,
      style: { fontFamily: 'Inter', fontSize: '16px' },
    },
    plotOptions: {
      series: {
        stacking: 'normal',
        marker: { enabled: false },
        events: {
          legendItemClick: event => {
            event.preventDefault();
          },
        },
      },
    },
    colors: seriesStackOrder.map(seriesName =>
      seriesName === PlanSeriesName.Covered
        ? theme.colors.primary['300']
        : theme.colors.primary['100'],
    ),
    legend: {
      enabled: false,
    },
    tooltip: {
      borderRadius: 10,
      shadow: false,
      useHTML: true,
      backgroundColor: theme.colors.neutral['700'],
      borderColor: theme.colors.neutral['700'],
      shape: 'square',
      style: {
        color: theme.colors.neutral['100'],
      },
      formatter() {
        return `
            <div style='display: flex; align-items: flex-start;'>
              <div style='width: 8px; height: 8px; border-radius: 4px; margin-top: 5px; margin-right: 4px; background: ${
                this.series.name === PlanSeriesName.Covered
                  ? `${theme.colors.primary['300']}`
                  : `${theme.colors.primary['100']}`
              }'></div>
              
              <div style='font-size: 12px; color: ${theme.colors.neutral['100']};'>
                <div style='margin-bottom: 4px;'>
                  <span>${
                    this.series.name === PlanSeriesName.Covered
                      ? 'Donation Covered:'
                      : 'Donation Not Covered:'
                  }</span>
                  <span style='font-weight: 600; margin-left: 4px;'>${this.x}</span>
                </div>
                <div style="display: flex; justify-content: flex-end">
                  <span style='font-weight: 600'>
                    ${formatCurrency(Number(this.y), {
                      average: true,
                      mantissa: 2,
                    }).toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          `;
      },
    },
    xAxis: {
      categories: Categories,
      tickWidth: 0,
      allowDecimals: false,
      labels: {
        style: {
          color: theme.colors.neutral['500'],
        },
      },
    },
    yAxis: {
      title: {
        text: null,
      },
      max: null,
      labels: {
        enabled: true,
        formatter() {
          return formatCurrency(Number(this.value), {
            average: true,
            mantissa: 2,
            optionalMantissa: true,
            trimMantissa: true,
          }).toUpperCase();
        },
        style: {
          color: theme.colors.neutral['500'],
        },
      },
      gridLineDashStyle: 'Solid',
      gridLineColor: theme.colors.neutral['200'],
    },
    series: chartSeries.map(_series => ({
      ..._series,
      type: 'column',
      lineWidth: 2,
      lineColor:
        _series.name === PlanSeriesName.Covered
          ? theme.colors.primary['500']
          : theme.colors.primary['300'],
      marker: {
        fillColor:
          _series.name === PlanSeriesName.Covered
            ? theme.colors.primary['500']
            : theme.colors.primary['300'],
      },
    })),
  };

  return {
    chartConfig,
  };
};
